package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.domain.model.DevicePlatform
import com.iptvplayer.shared.domain.repository.DeviceRepository
import com.iptvplayer.shared.presentation.state.DeviceUiState
import com.iptvplayer.shared.util.Constants
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

class DeviceViewModel(
    private val deviceRepository: DeviceRepository
) : ViewModel() {

    private val _state = MutableStateFlow(DeviceUiState())
    val state: StateFlow<DeviceUiState> = _state.asStateFlow()

    init {
        loadDevices()
        startHeartbeat()
    }

    private fun loadDevices() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            when (val result = deviceRepository.getDevices()) {
                is Result.Success -> {
                    _state.update {
                        it.copy(
                            devices = result.data,
                            currentDeviceId = deviceRepository.getLocalDeviceId(),
                            isLoading = false
                        )
                    }
                }
                is Result.Error -> {
                    _state.update { it.copy(isLoading = false, error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    fun registerDevice(name: String, platform: DevicePlatform) {
        viewModelScope.launch {
            when (val result = deviceRepository.registerDevice(name, platform)) {
                is Result.Success -> {
                    deviceRepository.saveLocalDeviceId(result.data.id)
                    loadDevices()
                }
                is Result.Error -> {
                    _state.update { it.copy(error = result.message) }
                }
                is Result.Loading -> {}
            }
        }
    }

    fun renameDevice(deviceId: String, name: String) {
        viewModelScope.launch {
            deviceRepository.renameDevice(deviceId, name)
            loadDevices()
        }
    }

    fun removeDevice(deviceId: String) {
        viewModelScope.launch {
            deviceRepository.removeDevice(deviceId)
            loadDevices()
        }
    }

    private fun startHeartbeat() {
        viewModelScope.launch {
            while (isActive) {
                val deviceId = deviceRepository.getLocalDeviceId()
                if (deviceId != null) {
                    deviceRepository.sendHeartbeat(deviceId)
                }
                delay(Constants.HEARTBEAT_INTERVAL_MS)
            }
        }
    }

    fun refresh() {
        loadDevices()
    }

    fun clearError() {
        _state.update { it.copy(error = null) }
    }
}
